import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

interface ModalContextValue {
  closing: boolean;
  close: () => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

function useModalContext(componentName: string): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    throw new Error(`${componentName} must be rendered inside <ModalOverlay>`);
  }
  return ctx;
}

interface ModalOverlayProps {
  onClose: () => void;
  children: ReactNode;
}

/**
 * 모달 오버레이. fade 애니메이션, Escape/바깥 클릭으로 닫기, body 스크롤 잠금을 담당.
 * 자식은 `<ModalPanel>` 을 중첩하거나 `useModalContext` 로 닫기 호출 가능.
 */
export function ModalOverlay({ onClose, children }: ModalOverlayProps) {
  const [closing, setClosing] = useState(false);

  const close = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 250);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  useEffect(() => {
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarW}px`;
    document.body.classList.add("modal-open");
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.classList.remove("modal-open");
    };
  }, []);

  return (
    <ModalContext.Provider value={{ closing, close }}>
      <div
        data-lenis-prevent
        className={`fixed inset-0 z-[1000] flex items-center justify-center px-6 py-6 max-md:px-4 max-md:py-4 bg-[rgba(10,20,30,0.6)] backdrop-blur-[8px] ${closing ? "animate-[pvModalFadeOut_0.25s_ease_forwards]" : "animate-[pvModalFadeIn_0.25s_ease]"}`}
        onClick={close}
      >
        {children}
      </div>
    </ModalContext.Provider>
  );
}

interface ModalPanelProps {
  children: ReactNode;
  /** 추가 Tailwind 클래스 — max-width, padding 등 */
  className?: string;
}

export function ModalPanel({ children, className = "" }: ModalPanelProps) {
  const { closing } = useModalContext("ModalPanel");
  return (
    <div
      className={`relative flex w-full max-h-full flex-col rounded-[16px] bg-white shadow-[0_16px_48px_rgba(0,0,0,0.15)] ${closing ? "animate-[newsSlideDown_0.25s_ease_forwards]" : "animate-[newsSlideUp_0.3s_ease-out]"} ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

interface ModalCloseButtonProps {
  ariaLabel?: string;
  buttonRef?: React.Ref<HTMLButtonElement>;
}

export function ModalCloseButton({ ariaLabel = "Close", buttonRef }: ModalCloseButtonProps) {
  const { close } = useModalContext("ModalCloseButton");
  return (
    <button
      ref={buttonRef}
      className="absolute top-2 right-2 z-[1] flex h-14 w-14 items-center justify-center rounded-full text-[var(--ip-text-muted)] transition-all hover:bg-[rgba(var(--ip-primary-glow),0.08)] hover:text-[var(--ip-text)] hover:scale-[1.3]"
      onClick={close}
      aria-label={ariaLabel}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="h-6 w-6"
      >
        <path d="M18 6L6 18M6 6L18 18" strokeLinecap="round" />
      </svg>
    </button>
  );
}
